import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Check, X } from "lucide-react";
import { SEO } from "../components/SEO";
import { TOOLS } from "../data/tools";
import { COMPARISON_PAGE } from "../constants/testIds";
import { getOutboundProps, resolveOutbound } from "../config/affiliateLinks";

const SORT_OPTIONS = [
  { key: "rating", label: "Highest Rated" },
  { key: "priceAsc", label: "Price: Low to High" },
  { key: "speed", label: "Fastest Generation" },
  { key: "value", label: "Best Value for Money" },
];

const parsePrice = (p) => {
  const n = parseFloat(String(p).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 999 : n;
};

export default function ComparisonMatrix() {
  const [sort, setSort] = useState("rating");

  const sorted = useMemo(() => {
    const arr = [...TOOLS];
    switch (sort) {
      case "priceAsc":
        return arr.sort((a, b) => parsePrice(a.pricing[0].price) - parsePrice(b.pricing[0].price));
      case "speed":
        return arr.sort((a, b) => b.ratingBreakdown.speed - a.ratingBreakdown.speed);
      case "value":
        return arr.sort((a, b) => b.ratingBreakdown.valueForMoney - a.ratingBreakdown.valueForMoney);
      default:
        return arr.sort((a, b) => b.rating - a.rating);
    }
  }, [sort]);

  return (
    <>
      <SEO title="AI Writing Tools Comparison Matrix" description="An interactive, sortable comparison of Phrasly.ai, Rytr, and Codex Leo AI — pricing, ratings, writing types, and integrations." />
      <section className="pt-36 pb-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-4">Interactive</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight max-w-2xl">Tools Comparison Matrix</motion.h1>
          <p className="text-zinc-400 mt-4 max-w-xl">Sort by what matters most to you. Every number here comes from our published testing rubric.</p>

          <div className="flex items-center gap-3 mt-8">
            <label htmlFor="sort-select" className="text-xs font-mono-tag uppercase tracking-wider text-zinc-500">Sort by</label>
            <select
              id="sort-select"
              data-testid={COMPARISON_PAGE.sortSelect}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-surface border border-white/10 text-white text-sm rounded-sm h-10 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="mt-8 overflow-x-auto scrollbar-hide rounded-sm border border-white/10">
            <table className="matrix-table w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-surface">
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400 sticky left-0 bg-surface z-10 min-w-[160px]">Tool</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Rating</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Starting Price</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Speed</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Value</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Chrome Ext.</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Zapier</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Best For</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400">Try It</th>
                  <th className="text-left px-5 py-4 font-mono-tag text-xs uppercase tracking-wider text-zinc-400"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((tool) => {
                  const outbound = getOutboundProps(tool.affiliateKey);
                  const resolved = resolveOutbound(tool.affiliateKey);
                  const isLive = !!(resolved && resolved.key === tool.affiliateKey);
                  return (
                  <tr key={tool.slug} data-testid={COMPARISON_PAGE.row(tool.slug)} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 sticky left-0 bg-inkblack z-10">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tool.color }} aria-hidden="true" />
                        <span className="text-white font-medium">{tool.name}</span>
                        {tool.editorsChoice && <span className="text-[10px] font-mono-tag uppercase px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30 whitespace-nowrap">Editor's Choice</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-silver"><Star className="w-3.5 h-3.5 fill-gold text-gold inline mr-1" aria-hidden="true" />{tool.rating}</td>
                    <td className="px-5 py-4 text-zinc-300 whitespace-nowrap">{tool.pricing[0].price}{tool.pricing[0].period}</td>
                    <td className="px-5 py-4 text-zinc-300">{tool.ratingBreakdown.speed}/5</td>
                    <td className="px-5 py-4 text-zinc-300">{tool.ratingBreakdown.valueForMoney}/5</td>
                    <td className="px-5 py-4">{tool.integrations.includes("Chrome Extension") ? <Check className="w-4 h-4 text-gold" aria-hidden="true" /> : <X className="w-4 h-4 text-zinc-600" aria-hidden="true" />}</td>
                    <td className="px-5 py-4">{tool.integrations.includes("Zapier") ? <Check className="w-4 h-4 text-gold" aria-hidden="true" /> : <X className="w-4 h-4 text-zinc-600" aria-hidden="true" />}</td>
                    <td className="px-5 py-4 text-zinc-400 whitespace-nowrap">{tool.bestFor}</td>
                    <td className="px-5 py-4">
                      {isLive && outbound ? (
                        <a
                          href={outbound.href}
                          target={outbound.target}
                          rel={outbound.rel}
                          data-testid={COMPARISON_PAGE.tryCta(tool.slug)}
                          className="h-9 px-4 bg-primary hover:bg-[#5C7CFA] text-white text-xs font-medium rounded-sm inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
                        >
                          Try Free <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                        </a>
                      ) : (
                        <Link
                          to={`/reviews/${tool.slug}`}
                          data-testid={COMPARISON_PAGE.tryCta(tool.slug)}
                          className="h-9 px-4 border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 text-xs font-medium rounded-sm inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
                        >
                          Read review <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                        </Link>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <Link to={`/reviews/${tool.slug}`} className="text-primary hover:text-[#5C7CFA] text-sm font-medium inline-flex items-center gap-1 whitespace-nowrap">
                        Review <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {sorted.map((tool) => (
              <div key={tool.slug} className="border border-white/10 rounded-sm p-6 bg-surface/40">
                <div className="flex items-center justify-between">
                  <span className="text-white font-display font-bold">{tool.name}</span>
                  <span className="flex items-center gap-1 text-sm text-silver"><Star className="w-3.5 h-3.5 fill-gold text-gold" aria-hidden="true" />{tool.rating}</span>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Writing types</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {tool.writingTypes.slice(0, 4).map((w) => (
                    <span key={w} className="text-[11px] font-mono-tag px-2 py-1 rounded-sm bg-white/5 text-zinc-400 border border-white/10">{w}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
